import { useCallback, useEffect, useRef } from 'react'
import type { JSX } from 'react'

import { Dialog } from '@buffer-mono/popcorn'

import { useUser } from '../../../../common/context/User'
import { useModalManager } from '../../../ModalManager/hooks/useModalManager'
import { Service } from '../../../../exports/Orchestrator/channelConnections/types'

import { CONNECT_PROFILE_CTA } from './constants'
import { isLinkedInProfile } from './helpers'
import { LinkedInCapabilitiesContent } from './LinkedInCapabilitiesContent'
import {
  trackLinkedInCapabilitiesModalCtaClicked,
  trackLinkedInCapabilitiesModalViewed,
} from './tracking'

export function LinkedInCapabilities(): JSX.Element | null {
  const user = useUser()
  const { dismissModal } = useModalManager()

  const channels = user.currentOrganization?.channels ?? []
  const linkedInProfiles = channels.filter(isLinkedInProfile)
  const hasTrackedModalViewed = useRef(false)

  useEffect(() => {
    if (hasTrackedModalViewed.current || !user.currentOrganization?.id) {
      return
    }

    trackLinkedInCapabilitiesModalViewed({
      linkedinProfileCount: linkedInProfiles.length,
      user,
    })
    hasTrackedModalViewed.current = true
  }, [linkedInProfiles.length, user])

  const handleConnectProfile = useCallback((): void => {
    trackLinkedInCapabilitiesModalCtaClicked({
      cta: 'connectLinkedInProfile',
      user,
    })

    const connectChannel = window.appshell?.actions?.connectChannel
    if (!connectChannel) {
      return
    }

    connectChannel({
      cta: CONNECT_PROFILE_CTA,
      selectedService: Service.linkedin,
    })
    dismissModal({ shouldUseTracking: false })
  }, [dismissModal, user])

  const handleOpenProfileSettings = useCallback(
    (channelId: string): void => {
      trackLinkedInCapabilitiesModalCtaClicked({
        channelId,
        cta: 'setup',
        user,
      })
      dismissModal({ shouldUseTracking: false })
    },
    [dismissModal, user],
  )

  const handleOpenChange = (open: boolean): void => {
    if (open) {
      return
    }

    trackLinkedInCapabilitiesModalCtaClicked({
      cta: 'close',
      user,
    })
    dismissModal()
  }

  return (
    <Dialog open={true} onOpenChange={handleOpenChange}>
      <LinkedInCapabilitiesContent
        linkedInProfiles={linkedInProfiles}
        onConnectProfile={handleConnectProfile}
        onOpenProfileSettings={handleOpenProfileSettings}
      />
    </Dialog>
  )
}
